import {
  Button,
  ButtonIconWithLabel,
  Icon_OpenNew24,
  Icon_TriangleDown,
  Menu,
  Spinner,
} from "@shapr3d/frontend-lib";
import toast from "react-hot-toast";

import { defaultPrivateSpaceName, defaultTeamSpaceName } from "../../../config";
import { useProject } from "../../../hooks/useProject";
import { useProjectActions } from "../../../hooks/useProjectActions";
import { useTracker } from "../../../hooks/useTracker";
import { useWebExport } from "../../../hooks/useWebExport";
import { ProjectRoleUtils } from "../../../types";
import { formatDate } from "../../../utils/helpers";
import { HeaderBreadCrumbs } from "../../HeaderBreadCrumbs/HeaderBreadCrumbs";
import { UserName } from "../../UserName/UserName";

import style from "./ProjectMetadata.module.css";

const contextMenuLocation = "project_page_project_title";
const spaceTypeNameMap: Record<string, string> = {
  private: defaultPrivateSpaceName,
  team: defaultTeamSpaceName,
};

const ProjectMetadataSection = () => {
  const { data: projectData } = useProject();
  const tracker = useTracker();
  const { handleEditInAppClick, handleCopyProjectLink, handleShareClick, handleDownloadClick } =
    useProjectActions();

  if (!projectData) {
    return null;
  }

  const showEditInAppButton = ProjectRoleUtils.isAtLeast(projectData.userRole, "editor");
  const canDownload = Boolean(
    projectData.latestRevision.fileDownload?.url && projectData.project.projectId
  );
  const projectSpaceName =
    projectData.space?.spaceName ?? spaceTypeNameMap[projectData.space?.spaceType ?? ""];
  const projectCreatedByUser = projectData.users?.find(
    (user) => user.userId === projectData.latestRevision.uploadedByUserId
  );

  const handleMetadataMenuOpen = () => {
    tracker.trackContextMenuOpened({
      location: contextMenuLocation,
    });
  };

  const handleMetadataCopyLink = async () => {
    tracker.trackContextMenuActed({
      action: "copy_link",
      location: contextMenuLocation,
    });

    const isCopied = await handleCopyProjectLink();
    if (isCopied) {
      toast.success("Copied to clipboard");
    } else {
      toast.error("Failed to copy link to clipboard");
    }
  };

  const handleMetadataShare = () => {
    tracker.trackContextMenuActed({
      action: "share",
      location: contextMenuLocation,
    });

    handleShareClick();
  };

  const handleMetadataDownload = () => {
    tracker.trackContextMenuActed({
      action: "download",
      location: contextMenuLocation,
    });

    handleDownloadClick();
  };

  return (
    <>
      <div className={style.projectData}>
        {projectSpaceName && (
          <div className={style.breadcrumbs}>
            <HeaderBreadCrumbs teamID={projectData.teamId} spaceName={projectSpaceName} />
          </div>
        )}
        <Menu
          className={style.menuWrapper}
          activator={
            <div role="button" className={style.title} title={projectData.project.name}>
              <span className={style.titleText}>{projectData.project.name}</span>
              <Icon_TriangleDown className={style.triangleDown} />
            </div>
          }
          onOpen={handleMetadataMenuOpen}
        >
          {showEditInAppButton && (
            <Menu.Item closeOnClick onClick={handleEditInAppClick}>
              <span>Edit in Shapr3D app</span>
            </Menu.Item>
          )}
          <Menu.Item closeOnClick onClick={handleMetadataCopyLink}>
            <span>Copy Link</span>
          </Menu.Item>
          {showEditInAppButton && (
            <Menu.Item closeOnClick onClick={handleMetadataShare}>
              <span>Share...</span>
            </Menu.Item>
          )}
          {canDownload && (
            <Menu.Item closeOnClick onClick={handleMetadataDownload}>
              <span>Download as .shapr</span>
            </Menu.Item>
          )}
        </Menu>
        <div>
          <div className={style.lastEdited}>
            Last edited: {formatDate(projectData.latestRevision.lastModifiedAt)}
          </div>
          <UserName user={projectCreatedByUser} fallback={"-"} color="gray" />
        </div>
        {showEditInAppButton && (
          <Button
            variant="grayscale"
            className={style.editInAppButton}
            onClick={handleEditInAppClick}
          >
            <ButtonIconWithLabel icon={<Icon_OpenNew24 />} label="Edit in Shapr3D app" />
          </Button>
        )}
      </div>
      <div className={style.divider}></div>
    </>
  );
};

const WebExportMetadataSection = () => {
  const { data: webExportData } = useWebExport();
  const tracker = useTracker();
  const { handleCopyViewLink } = useProjectActions();

  if (!webExportData) {
    return null;
  }

  const webExport = webExportData.webExport;

  const handleMetadataMenuOpen = () => {
    tracker.trackContextMenuOpened({
      location: contextMenuLocation,
    });
  };

  const handleMetadataCopyLink = async () => {
    tracker.trackContextMenuActed({
      action: "copy_link",
      location: contextMenuLocation,
    });

    const isCopied = await handleCopyViewLink();
    if (isCopied) {
      toast.success("Copied to clipboard");
    } else {
      toast.error("Failed to copy link to clipboard");
    }
  };

  return (
    <>
      <div className={style.projectData}>
        <Menu
          className={style.menuWrapper}
          activator={
            <div role="button" className={style.title} title={webExport.title}>
              <span className={style.titleText}>{webExport.title}</span>
              <Icon_TriangleDown className={style.triangleDown} />
            </div>
          }
          onOpen={handleMetadataMenuOpen}
        >
          <Menu.Item closeOnClick onClick={handleMetadataCopyLink}>
            <span>Copy Link</span>
          </Menu.Item>
        </Menu>

        <div>
          <div className={style.lastEdited}>Published: {formatDate(webExport.uploadedAt)}</div>
        </div>
      </div>
      <div className={style.divider}></div>
    </>
  );
};

export const ProjectMetadata = () => {
  const { data: projectData, isLoading: isProjectLoading } = useProject();
  const { data: webExportData, isLoading: isWebExportLoading } = useWebExport();

  const isLoading = !projectData && !webExportData && (isProjectLoading || isWebExportLoading);

  if (isLoading) {
    return (
      <>
        <div className={style.loadingContainer}>
          <Spinner size={20} />
        </div>
        <div className={style.divider}></div>
      </>
    );
  }

  if (projectData) {
    return <ProjectMetadataSection />;
  }

  if (webExportData) {
    return <WebExportMetadataSection />;
  }

  return null;
};
